/**
 * useGamesAndProps Hook
 * Shared between web and mobile for fetching games and bettable props.
 * Two-step fetch: games first, then props with gameIds.
 */

import { useCallback, useRef, useState } from 'react';
import type { HttpClient } from '../api/httpClient';
import { gamesWithKnownLeague, type LeagueKey } from '../league';
import type { ChoosableBookId } from '../sportsbook';

// =============================================================================
// Types
// =============================================================================

export interface Game {
  id: string;
  sport_key: LeagueKey;
  sport_title?: string;
  commence_time: string;
  away_team: string;
  home_team: string;
  [key: string]: unknown;
}

export interface BettableProp {
  id: string;
  playerName?: string;
  team?: string;
  propType: string;
  line?: number;
  overUnder?: string;
  odds: string;
  gameId: string;
  gameDate?: string;
  [key: string]: unknown;
}

/** A game as the API sends it, before its sport_key is checked. */
export interface ListedGame {
  id: string;
  sport_key?: unknown;
  sport_title?: string;
  commence_time: string;
  away_team: string;
  home_team: string;
  [key: string]: unknown;
}

export interface UseGamesAndPropsOptions {
  httpClient: HttpClient;
  /** Filter games to a specific league. If omitted, loads all leagues. */
  league?: string;
  /** Reports a listed game whose sport_key names no league; the game is left out. */
  onUnknownLeague: (game: ListedGame) => void;
}

export interface UseGamesAndPropsResult {
  games: Game[];
  props: BettableProp[];
  loading: boolean;
  error: string | null;
  fetchGamesAndProps: (sportsbook: ChoosableBookId) => Promise<void>;
}

// =============================================================================
// Helpers
// =============================================================================

/**
 * Safe JSON parsing helper
 */
async function safeParseJson(response: Response): Promise<unknown> {
  const text = await response.text();
  try {
    return JSON.parse(text);
  } catch {
    throw new Error(`Invalid JSON response: ${text.substring(0, 100)}`);
  }
}

// Module-level cache to avoid re-fetching on every modal open
const apiCache = {
  games: new Map<string, { data: Game[]; ts: number }>(), // keyed by league ('' = all)
  props: new Map<string, { data: BettableProp[]; ts: number }>(),
};
const GAMES_TTL = 5 * 60 * 1000; // 5 min
const PROPS_TTL = 2 * 60 * 1000; // 2 min

/** Clear cached data (e.g., after adding a prop or when user wants fresh data) */
export function invalidatePropsCache(): void {
  apiCache.games.clear();
  apiCache.props.clear();
}

// =============================================================================
// Hook
// =============================================================================

export function useGamesAndProps(options: UseGamesAndPropsOptions): UseGamesAndPropsResult {
  const { httpClient, league, onUnknownLeague } = options;

  const [games, setGames] = useState<Game[]>([]);
  const [props, setProps] = useState<BettableProp[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // A league or book switch starts a new fetch while the last is in flight; only the newest may write.
  const latestRequest = useRef(0);

  const fetchGamesAndProps = useCallback(
    async (sportsbook: ChoosableBookId) => {
      const request = ++latestRequest.current;
      const isLatest = () => request === latestRequest.current;
      const now = Date.now();

      // Check if both caches are fresh -- return instantly without loading state
      const gamesKey = league || '';
      const gamesEntry = apiCache.games.get(gamesKey);
      const gamesCached = gamesEntry && now - gamesEntry.ts < GAMES_TTL;
      const propsKey = `${sportsbook}:${gamesKey}`;
      const propsEntry = apiCache.props.get(propsKey);
      const propsCached = propsEntry && now - propsEntry.ts < PROPS_TTL;

      if (gamesCached && propsCached) {
        setGames(gamesEntry!.data);
        setProps(propsEntry!.data);
        setError(null);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        // Fetch games (use cache if fresh)
        let fetchedGames: Game[];
        if (gamesCached) {
          fetchedGames = gamesEntry!.data;
        } else {
          const gamesUrl = league
            ? `/api/games?league=${encodeURIComponent(league)}`
            : '/api/games';
          const gamesRes = await httpClient.rawFetch(gamesUrl);
          const gamesData = (await safeParseJson(gamesRes)) as {
            error?: string;
            games?: ListedGame[];
          };
          if (gamesData.error) throw new Error(gamesData.error);
          fetchedGames = gamesWithKnownLeague(gamesData.games || [], onUnknownLeague);
          apiCache.games.set(gamesKey, { data: fetchedGames, ts: Date.now() });
        }
        if (!isLatest()) return;

        if (fetchedGames.length === 0) {
          setGames([]);
          setProps([]);
          setError('No games available today');
          return;
        }

        setGames(fetchedGames);

        // Fetch props (use cache if fresh)
        let fetchedProps: BettableProp[];
        if (propsCached) {
          fetchedProps = propsEntry!.data;
        } else {
          const gameIds = fetchedGames.map((g) => g.id);
          const propsRes = await httpClient.rawFetch(
            `/api/props?gameIds=${gameIds.join(',')}&sportsbook=${sportsbook}`,
          );
          const propsData = (await safeParseJson(propsRes)) as {
            error?: string;
            props?: BettableProp[];
          };
          if (propsData.error) throw new Error(propsData.error);
          fetchedProps = propsData.props || [];
          apiCache.props.set(propsKey, { data: fetchedProps, ts: Date.now() });
        }
        if (!isLatest()) return;

        setProps(fetchedProps);
      } catch (err) {
        if (!isLatest()) return;
        const message = err instanceof Error ? err.message : 'Failed to load games and props';
        setError(message);
      } finally {
        if (isLatest()) setLoading(false);
      }
    },
    [httpClient, league, onUnknownLeague],
  );

  return {
    games,
    props,
    loading,
    error,
    fetchGamesAndProps,
  };
}
